import { useMemo } from 'react';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { MonitorType } from '../../../../shared/types/monitor/MonitorType';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import { ProjectStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { canShowModel } from '../../../../shared/utils/utils';
import { getMonitorTypesForChatType, MODEL_KEY_TO_CHAT_TYPES } from '../../../../shared/helpers/MonitorTypeHelpers';
import { Nullable } from 'primereact/ts-helpers';

/**
 * Maps canShowModel keys to all ChatType values that represent that model
 * in MonitorPromptsKeywordsStatsResponse.chatType.
 */
const isEndDateToday = (range: Nullable<(Date | null)[]>): boolean => {
  if (!range || !range[1]) {
    return false;
  }

  const endDate = range[1];
  const today = new Date();

  return (
    endDate.getFullYear() === today.getFullYear() &&
    endDate.getMonth() === today.getMonth() &&
    endDate.getDate() === today.getDate()
  );
};

/**
 * Returns a list of canShowModel keys for models that should show a skeleton
 * in PromptsTable per-model columns.
 *
 * Logic:
 * 1. If accountResponse is null → return all model keys (safe fallback while loading).
 * 2. Build monitorTypesWithData: for each stats entry with a non-null averagePosition,
 *    convert chatType → MonitorType[] via getMonitorTypesForChatType and add to a Set.
 * 3. For each model key that is enabled (canShowModel):
 *    - Find all MonitorTypes for its chatTypes via getMonitorTypesForChatType.
 *    - If none of those MonitorTypes are in monitorTypesWithData → model has no data.
 * 4. A model key is returned (skeleton shown) only when it has no data AND
 *    (isStatsLoading === true OR the selected date range ends today).
 *    If neither loading condition holds and a model has no data, it is not a skeleton —
 *    it simply has no data for that period.
 */
export const useModelsWithoutData = (
  accountResponse: AccountResponse | null,
  keywordsDailyStatsForProject: ProjectStatsResponse,
  isStatsLoading: boolean,
  range: Nullable<(Date | null)[]>,
  // Owner account's models when a superadmin views a client's project — so skeletons/columns match the
  // client's models, not the superadmin's. Falls back to the logged-in account's features.
  featuresOverride?: string[] | null
): string[] =>
  useMemo(() => {
    // If account not yet loaded, fall back to all keys to show skeletons
    if (!accountResponse) {
      return MODEL_KEY_TO_CHAT_TYPES.map(({ key }) => key);
    }

    const features = featuresOverride ?? accountResponse.features;

    // Step 1 — build a Set of MonitorTypes that have at least one non-null averagePosition
    const stats = keywordsDailyStatsForProject.monitorPromptsKeywordsStatsResponse;
    const monitorTypesWithData = new Set<MonitorType>();
    stats.forEach((entry) => {
      if (entry.averagePosition !== null && entry.chatType) {
        getMonitorTypesForChatType(entry.chatType as ChatType).forEach((mt) =>
          monitorTypesWithData.add(mt)
        );
      }
    });

    // Step 2 — determine the loading condition: skeleton is only warranted when data may still arrive
    const shouldShowSkeleton = isStatsLoading || isEndDateToday(range);

    // Step 3 — find enabled model keys that have no MonitorType with data
    return MODEL_KEY_TO_CHAT_TYPES.filter(({ key, chatTypes }) => {
      if (!canShowModel(key, features)) {
        return false;
      }

      // Gather all MonitorTypes for this model's chatTypes
      const modelMonitorTypes: MonitorType[] = chatTypes.flatMap((ct) =>
        getMonitorTypesForChatType(ct)
      );

      const hasData = modelMonitorTypes.some((mt) => monitorTypesWithData.has(mt));

      // Return true (skeleton) only when there is no data AND data may still arrive
      return !hasData && shouldShowSkeleton;
    }).map(({ key }) => key);
  }, [accountResponse, keywordsDailyStatsForProject, isStatsLoading, range, featuresOverride]);